/**
 * Shared utilities for client and server loggers
 * - Common constants, functions, and validation logic
 */

/**
 * Standard log levels in priority order (lower index = higher priority)
 */
const LEVELS = ["ERROR", "WARN", "INFO", "DEBUG"]

/**
 * Determines if a message should be logged based on the current log level
 * @param {string} level - Log level to check (ERROR, WARN, INFO, DEBUG)
 * @param {string} configLevel - Current configured log level
 * @return {boolean} True if the message should be logged
 */
function shouldLog(level, configLevel) {
  const currentLevelIndex = LEVELS.indexOf(configLevel)
  const messageLevelIndex = LEVELS.indexOf(level)

  // If level not found, default to INFO (index 2)
  const currentIndex = currentLevelIndex >= 0 ? currentLevelIndex : 2
  const messageIndex = messageLevelIndex >= 0 ? messageLevelIndex : 2

  // Lower index = higher priority (ERROR=0 is highest, DEBUG=3 is lowest)
  return messageIndex <= currentIndex
}

/**
 * Sanitizes data for logging by controlling depth/size and handling circular references
 * @param {any} value - Value to sanitize
 * @param {number} depth - Current depth in object traversal
 * @param {WeakSet} seen - Set to track circular references
 * @param {object} config - Configuration object with maxDepth, maxArrayLength, maxStringLength
 * @return {any} Sanitized value safe for logging
 */
function sanitize(value, depth = 0, seen = new WeakSet(), config = {}) {
  const { maxDepth = 4, maxArrayLength = 50, maxStringLength = 1000 } = config

  try {
    if (value == null) return value

    if (typeof value === "string") {
      if (value.length > maxStringLength) {
        return value.slice(0, maxStringLength) + "…"
      }
      return value
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return value
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      }
    }

    if (typeof value === "function") {
      return `[Function ${value.name || "anonymous"}]`
    }

    if (typeof value === "object") {
      if (seen.has(value)) return "[Circular]"
      seen.add(value)

      // Check depth limit for both arrays and objects
      if (depth >= maxDepth) {
        return Array.isArray(value) ? "[Array]" : "[Object]"
      }

      if (Array.isArray(value)) {
        const out = []
        const len = Math.min(value.length, maxArrayLength)
        for (let i = 0; i < len; i++) {
          out.push(sanitize(value[i], depth + 1, seen, config))
        }
        if (value.length > maxArrayLength) {
          out.push(`[+${value.length - maxArrayLength} more]`)
        }
        return out
      }

      // Check if object has toJSON method and call it if depth allows
      if (value.toJSON && typeof value.toJSON === "function") {
        try {
          const jsonResult = value.toJSON()
          // Replace the original object with the toJSON result
          return sanitize(jsonResult, depth, seen, config)
        } catch (e) {
          // If toJSON fails, fall back to normal object serialization
        }
      }

      const out = {}
      for (const [k, v] of Object.entries(value)) {
        out[k] = sanitize(v, depth + 1, seen, config)
      }
      return out
    }

    return value
  } catch (e) {
    return "[Unserializable]"
  }
}

/**
 * Writes log message to console with appropriate console method based on level
 * @param {string} level - Log level (ERROR, WARN, INFO, DEBUG)
 * @param {string} contextStr - Context string for the log message
 * @param {string} message - Log message
 * @param {object} meta - Additional metadata to log
 * @param {boolean} consoleEnabled - Whether console logging is enabled
 * @return {void}
 */
function consoleWrite(level, contextStr, message, meta, consoleEnabled = true) {
  if (!consoleEnabled) return
  try {
    const consoleMessage = `[${level}]${
      contextStr ? ` [${contextStr}]` : ""
    } ${message}`
    switch (level) {
      case "ERROR":
        console.error(consoleMessage, meta)
        break
      case "WARN":
        console.warn(consoleMessage, meta)
        break
      case "INFO":
        console.info(consoleMessage, meta)
        break
      case "DEBUG":
        console.debug(consoleMessage, meta)
        break
      default:
        console.log(consoleMessage, meta)
    }
  } catch (e) {
    // Never allow console issues to break the app
    try {
      // Best-effort minimal fallback
      console && console.log && console.log(`[${level}] ${message}`)
    } catch (_) {
      // no-op
    }
  }
}

/**
 * Redacts email for privacy - shows first/last char of local part only
 * @param {string} email - Email to redact
 * @return {string} Redacted email (e.g., "j***n@example.com")
 */
function redactEmail(email) {
  if (!email?.includes("@")) return ""
  const [localPart, domain] = email.split("@")
  if (!localPart || !domain) return ""
  const redactedLocalPart = localPart.charAt(0) + "***" + localPart.at(-1)
  return `${redactedLocalPart}@${domain}`
}

/**
 * Extracts guest ID from anonymous Okta token stored in localStorage
 * @return {string} Guest ID or empty string if not found/invalid
 */
function getGuestIdFromToken() {
  if (typeof window === "undefined") return ""

  try {
    const anonymousOktaToken = localStorage.getItem(
      "okta-anonymous-access-token"
    )
    if (!anonymousOktaToken) return ""

    const [, payload] = anonymousOktaToken.split(".")
    if (!payload) return ""

    const oktaObject = JSON.parse(atob(payload)) || {}
    return oktaObject.gId || ""
  } catch (e) {
    try {
      console?.warn?.("Anonymous Okta Token cannot be decoded:", e.message)
    } catch (_) {
      // swallow
    }
    return ""
  }
}

/**
 * Enriches log data with user identification (authenticated user or guest)
 * Requires Redux store to be passed in
 * @param {object} logData - Base log data to enrich
 * @param {object} store - Redux store instance
 * @return {object} Enriched log data with user/guestID field
 */
function enrichWithUserContext(logData, store) {
  if (!store) return logData

  try {
    const state = store?.getState() || {}
    const {
      auth: { user: { lowercaseEmail = "" } = {}, isAuth = false } = {},
    } = state

    if (isAuth && lowercaseEmail) {
      return {
        ...logData,
        user: redactEmail(lowercaseEmail),
      }
    } else {
      const guestID = getGuestIdFromToken()
      return guestID ? { ...logData, guestID } : logData
    }
  } catch (e) {
    // Never let user context enrichment break logging
    return logData
  }
}

/**
 * Builds base log data structure with common fields
 * @param {string} instanceContext - Logger instance context
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {object} config - Configuration object with globalContext, getUserId
 * @param {string} pathname - Pathname for client
 * @return {object} Base structured log data object
 */
function buildBaseLogData(instanceContext, level, message, config, pathname) {
  const { globalContext = "", getUserId = null } = config
  let userId
  if (typeof getUserId === "function") {
    try {
      userId = getUserId()
    } catch (_) {
      // swallow user resolver errors
      userId = undefined
    }
  }

  return {
    level,
    message,
    context: [globalContext, instanceContext].filter(Boolean).join(":"),
    timestamp: new Date().toISOString(),
    page: pathname,
    userId,
  }
}

// ES module exports for client-side
export {
  LEVELS,
  shouldLog,
  sanitize,
  consoleWrite,
  buildBaseLogData,
  redactEmail,
  getGuestIdFromToken,
  enrichWithUserContext,
}
